import { forwardRef, useId, type SelectHTMLAttributes } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SelectOptionItem {
  value: string
  label: string
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string
  error?: string
  hint?: string
  /** Primeira opcao, sem valor — o "Selecione..." que some da lista depois da escolha. */
  placeholder?: string
  options: readonly SelectOptionItem[]
}

/**
 * Select do design system.
 *
 * Existe para que os campos de escolha parem de ser `<select>` cru com estilo colado inline em
 * cada tela: `appearance-none` + chevron proprio deixam o controle com a mesma altura, raio,
 * fundo e anel de foco do `Input`, em vez do widget do sistema operacional ao lado dos campos
 * customizados. O elemento por baixo continua sendo um `<select>` nativo — teclado, busca por
 * digitacao e o seletor em roda do celular seguem funcionando de graca.
 */
const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, hint, placeholder, options, id, ...props }, ref) => {
    const generatedId = useId()
    const selectId = id || generatedId
    const describedById = error ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedById}
            className={cn(
              'h-10 w-full appearance-none rounded-lg border px-3 pr-9 text-sm',
              'bg-neutral-50 dark:bg-neutral-900 text-neutral-900 dark:text-white',
              'transition-colors duration-200',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
              'disabled:cursor-not-allowed disabled:opacity-60',
              error
                ? 'border-red-500 dark:border-red-400 focus:ring-red-400'
                : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 dark:hover:border-neutral-500',
              className
            )}
            {...props}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown
            aria-hidden="true"
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
          />
        </div>
        {error && (
          <p id={`${selectId}-error`} className="mt-1.5 text-sm text-red-500 dark:text-red-400">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${selectId}-hint`} className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
            {hint}
          </p>
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'

export { Select }
